import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { IMAGERY } from "../lib/imagery";
import { accent } from "../lib/accent";

function CapabilityBlock({ id, roman, title, body, items, image, reverse }) {
  return (
    <article
      data-testid={`capability-${id}`}
      className="grid grid-cols-12 gap-8 lg:gap-12 items-center"
    >
      <div
        className={`col-span-12 lg:col-span-7 ${
          reverse ? "lg:order-2" : ""
        } space-y-8`}
      >
        <div className="flex items-baseline gap-6">
          <span className="font-serif text-5xl text-champagne">{roman}</span>
          <span className="block flex-1 h-px bg-obsidian/15" />
        </div>
        <h3 className="display-h2 max-w-[18ch]">{title}</h3>
        <p className="body-lg max-w-2xl">{body}</p>
        <ul className="space-y-3 pt-4 border-t border-white/8">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex gap-4 py-2 text-foreground/85"
              data-testid={`cap-${id}-item-${i}`}
            >
              <Check
                size={16}
                className="mt-1.5 text-champagne flex-shrink-0"
              />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>
      <div
        className={`col-span-12 lg:col-span-5 ${
          reverse ? "lg:order-1" : ""
        } relative`}
      >
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-obsidian/15" />
          <div className="absolute top-5 left-5 right-5 flex items-center gap-3">
            <span className="block w-8 h-px bg-champagne" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white">
              Mandate {roman}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Capabilities() {
  const { t } = useLang();

  return (
    <>
      <section
        data-testid="capabilities-hero"
        className="relative -mt-20 pt-40 pb-24 md:pb-32 bg-obsidian text-white overflow-hidden"
      >
        <img
          src={IMAGERY.solutionsHero}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-x relative z-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <p className="eyebrow-light">{t.capabilities.eyebrow}</p>
            <h1 className="display-h1 mt-6 max-w-[18ch] text-white">
              {accent(t.capabilities.title)}
            </h1>
            <p className="font-sans text-lg md:text-xl lg:text-2xl text-white/85 mt-8 max-w-3xl leading-relaxed font-light animate-fade-in delay-200">
              {t.capabilities.subtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="container-x py-28 md:py-36 space-y-32">
        <CapabilityBlock
          id="1"
          roman={t.capabilities.block1Roman}
          title={t.capabilities.block1Title}
          body={t.capabilities.block1Body}
          items={t.capabilities.block1Items}
          image={IMAGERY.mnaAdvisory}
        />
        <CapabilityBlock
          id="2"
          roman={t.capabilities.block2Roman}
          title={t.capabilities.block2Title}
          body={t.capabilities.block2Body}
          items={t.capabilities.block2Items}
          image={IMAGERY.valuationData}
          reverse
        />
        <CapabilityBlock
          id="3"
          roman={t.capabilities.block3Roman}
          title={t.capabilities.block3Title}
          body={t.capabilities.block3Body}
          items={t.capabilities.block3Items}
          image={IMAGERY.skyline}
        />
      </section>

      <section className="bg-obsidian text-white">
        <div className="container-x py-24 md:py-32 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="eyebrow-light">{t.home.ctaEyebrow}</p>
            <h2 className="display-h2 text-white mt-5 max-w-[18ch]">
              {accent(t.capabilities.ctaTitle)}
            </h2>
            <p className="body-lg text-white/75 mt-6 max-w-2xl">
              {t.capabilities.ctaBody}
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Link
              to="/contact"
              data-testid="capabilities-cta"
              className="group gold-btn"
            >
              {t.capabilities.ctaBtn}
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
